/**
 * Core primitives shared across the api / events / jobs / config / rpc
 * modules. Wire shapes here mirror the vendored desktop app byte-for-byte
 * (camelCase, forward-slash paths — coupling contract §12.1) so that the
 * desktop-compatible `/api/v1` surface and the store→SSE bridge can be
 * typed against a single definition.
 *
 * Sources of truth (vendor/upstream, READ-ONLY):
 *  - src-tauri/src/api_server.rs        (ApiFileNode, ProjectEntry, sanitized reviews, graph)
 *  - src-tauri/src/commands/search.rs   (ProjectSearchResult / Response, SearchImageRef)
 *  - src-tauri/src/commands/file_sync.rs(FileChangeTask / Queue / RescanResult)
 *  - src/lib/ingest-queue.ts            (IngestTask)
 *  - src/lib/dedup-queue.ts + dedup.ts  (DedupTask, DuplicateGroup)
 *  - src/stores/*.ts                    (ActivityItem, ResearchTask, ReviewItem, LintItem, chat shapes)
 */
import { z } from "zod";
export const JsonValueSchema = z.lazy(() => z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(JsonValueSchema),
    z.record(JsonValueSchema),
]));
export const JsonObjectSchema = z.record(JsonValueSchema);
// ─── Scalars ─────────────────────────────────────────────────────────────
export const UuidSchema = z.string().uuid();
/** Milliseconds since the Unix epoch — the engine's native timestamp unit. */
export const EpochMsSchema = z.number().int().nonnegative();
/** ISO-8601 timestamp with offset — the Postgres/control-plane unit. */
export const IsoDateTimeSchema = z.string().datetime({ offset: true });
/**
 * Project reference accepted by the desktop-compatible API: a project
 * UUID, a project path, or the literal `current`
 * (api_server.rs `resolve_project`). Server-side, UUIDs are canonical;
 * `current` resolves to the caller's per-user last-opened project.
 */
export const ProjectRefSchema = z.string().min(1);
/**
 * Forward-slash relative path inside a project
 * (e.g. `wiki/entities/foo.md`, `raw/sources/paper.pdf`).
 * Traversal is rejected server-side via the safe_join jail (F-140);
 * the schema enforces the cheap syntactic invariants.
 */
export const ProjectRelPathSchema = z
    .string()
    .min(1)
    .refine((p) => !p.includes("\\"), {
    message: "Paths must use forward slashes",
})
    .refine((p) => !p.split("/").some((seg) => seg === ".." || seg === "."), { message: "Path traversal segments are not allowed" });
// ─── Response envelope ───────────────────────────────────────────────────
/**
 * Every JSON response of the desktop API is `{ ok: true, ... }` or
 * `{ ok: false, error }` (api_server.rs `ok()` / `err()`). The server
 * keeps that envelope for compatibility and extends the error side with
 * an optional machine-readable `code` and structured `details`.
 */
export const ErrorEnvelopeSchema = z.object({
    ok: z.literal(false),
    error: z.string(),
    /** Machine-readable error code (server extension; absent on desktop). */
    code: z.string().optional(),
    /** Structured detail payload (server extension). */
    details: JsonValueSchema.optional(),
});
export const OkEnvelopeSchema = z.object({ ok: z.literal(true) });
/** Well-known error codes used by gateway/services (server extension). */
export const ErrorCodeSchema = z.enum([
    "unauthorized",
    "forbidden",
    "not_found",
    "bad_request",
    "conflict",
    "payload_too_large",
    "unsupported_media_type",
    "rate_limited",
    "quota_exceeded",
    "service_disabled",
    "stale_fencing_token",
    "runtime_unavailable",
    "internal",
]);
// ─── Projects & file trees ───────────────────────────────────────────────
/** Engine-side project shape (src/types/wiki.ts WikiProject + TS id). */
export const WikiProjectSchema = z.object({
    id: z.string().optional(),
    name: z.string(),
    path: z.string(),
});
/** `GET /api/v1/projects` entry (api_server.rs ProjectEntry). */
export const ProjectEntrySchema = z.object({
    id: z.string(),
    name: z.string(),
    path: z.string(),
    current: z.boolean(),
    /** Owning workspace (gateway only; the desktop engine omits it). */
    tenantId: z.string().optional(),
});
export const FileNodeSchema = z.lazy(() => z.object({
    name: z.string(),
    path: z.string(),
    isDir: z.boolean(),
    children: z.array(FileNodeSchema).optional(),
}));
export const ApiFileNodeSchema = z.lazy(() => z.object({
    name: z.string(),
    path: z.string(),
    isDir: z.boolean(),
    size: z.number().int().nonnegative().nullable(),
    children: z.array(ApiFileNodeSchema).nullable(),
}));
// ─── Search (commands/search.rs) ─────────────────────────────────────────
export const SearchImageRefSchema = z.object({
    url: z.string(),
    alt: z.string(),
});
export const ProjectSearchResultSchema = z.object({
    path: z.string(),
    title: z.string(),
    snippet: z.string(),
    titleMatch: z.boolean(),
    score: z.number(),
    /** Present only for vector/hybrid hits (skip_serializing_if in Rust). */
    /** Punteggio di ORDINAMENTO della pagina: miscelato, satura a 1.0. */
    vectorScore: z.number().optional(),
    /**
     * Pertinenza: `1/(1+distanza)` del chunk migliore. Non dipende dalla
     * lunghezza della pagina, quindi e l'unico dei due confrontabile con una
     * soglia. Assente sui risultati trovati solo per parole chiave.
     */
    relevance: z.number().optional(),
    images: z.array(SearchImageRefSchema),
    /** Present only when `includeContent: true` was requested. */
    content: z.string().optional(),
});
export const SearchModeSchema = z.enum(["keyword", "vector", "hybrid"]);
export const ProjectSearchResponseSchema = z.object({
    mode: SearchModeSchema,
    results: z.array(ProjectSearchResultSchema),
    tokenHits: z.number().int().nonnegative(),
    vectorHits: z.number().int().nonnegative(),
});
// ─── Graph (api_server.rs ApiGraphNode / ApiGraphEdge) ───────────────────
export const GraphNodeSchema = z.object({
    id: z.string(),
    label: z.string(),
    nodeType: z.string(),
    path: z.string(),
    linkCount: z.number().int().nonnegative(),
});
export const GraphEdgeSchema = z.object({
    source: z.string(),
    target: z.string(),
    weight: z.number(),
});
// ─── Reviews ─────────────────────────────────────────────────────────────
/** Full engine-side review item (src/stores/review-store.ts). */
export const ReviewOptionSchema = z.object({
    label: z.string(),
    action: z.string(),
});
export const ReviewItemTypeSchema = z.enum([
    "contradiction",
    "duplicate",
    "missing-page",
    "confirm",
    "suggestion",
]);
export const ReviewItemSchema = z.object({
    id: z.string(),
    type: ReviewItemTypeSchema,
    title: z.string(),
    description: z.string(),
    sourcePath: z.string().optional(),
    affectedPages: z.array(z.string()).optional(),
    searchQueries: z.array(z.string()).optional(),
    options: z.array(ReviewOptionSchema),
    resolved: z.boolean(),
    resolvedAction: z.string().optional(),
    createdAt: EpochMsSchema,
});
/**
 * API-sanitized review item — the field allowlist that
 * api_server.rs `sanitize_review_item` exposes. Every field is optional
 * because the sanitizer copies fields only when present and well-typed;
 * `type` is a plain string on the wire (no enum enforcement upstream).
 */
export const SanitizedReviewItemSchema = z.object({
    id: z.string().optional(),
    type: z.string().optional(),
    title: z.string().optional(),
    description: z.string().optional(),
    sourcePath: z.string().optional(),
    affectedPages: z.array(z.string()).optional(),
    searchQueries: z.array(z.string()).optional(),
    options: z
        .array(z.object({
        label: z.string().optional(),
        action: z.string().optional(),
    }))
        .optional(),
    resolved: z.boolean().optional(),
    resolvedAction: z.string().optional(),
    createdAt: z.number().optional(),
    /** Review redesign: when / why the system or a decision closed it. */
    resolvedAt: z.number().optional(),
    resolvedNote: z.string().optional(),
    /** The source as a project-relative path, resolved by the server against the project root. */
    sourceRel: z.string().optional(),
});
export const ReviewStatusFilterSchema = z.enum([
    "unresolved",
    "pending",
    "resolved",
    "all",
]);
// ─── Lint (src/stores/lint-store.ts + src/lib/lint.ts) ───────────────────
export const LintTypeSchema = z.enum([
    "orphan",
    "broken-link",
    "no-outlinks",
    "semantic",
]);
export const LintSeveritySchema = z.enum(["warning", "info"]);
export const LintItemSchema = z.object({
    id: z.string(),
    type: LintTypeSchema,
    severity: LintSeveritySchema,
    page: z.string(),
    detail: z.string(),
    affectedPages: z.array(z.string()).optional(),
    brokenTarget: z.string().optional(),
    suggestedTarget: z.string().optional(),
    suggestedSource: z.string().optional(),
    createdAt: EpochMsSchema,
});
// ─── Activity (src/stores/activity-store.ts) ─────────────────────────────
export const ActivityItemSchema = z.object({
    id: z.string(),
    type: z.enum(["ingest", "lint", "query"]),
    title: z.string(),
    status: z.enum(["running", "done", "error"]),
    detail: z.string(),
    filesWritten: z.array(z.string()),
    createdAt: EpochMsSchema,
});
// ─── Research (src/stores/research-store.ts, src/lib/web-search.ts) ──────
export const WebSearchResultSchema = z.object({
    title: z.string(),
    url: z.string(),
    snippet: z.string(),
    source: z.string(),
});
export const ResearchTaskStatusSchema = z.enum([
    "queued",
    "searching",
    "synthesizing",
    "saving",
    "done",
    "error",
]);
export const ResearchTaskSchema = z.object({
    id: z.string(),
    topic: z.string(),
    searchQueries: z.array(z.string()).optional(),
    status: ResearchTaskStatusSchema,
    webResults: z.array(WebSearchResultSchema),
    synthesis: z.string(),
    savedPath: z.string().nullable(),
    error: z.string().nullable(),
    createdAt: EpochMsSchema,
});
// ─── Chat (src/stores/chat-store.ts) ─────────────────────────────────────
export const MessageImageSchema = z.object({
    mediaType: z.string(),
    dataBase64: z.string(),
});
export const MessageReferenceSchema = z.object({
    title: z.string(),
    path: z.string(),
    kind: z.enum(["wiki", "external"]).optional(),
    source: z.string().optional(),
    url: z.string().optional(),
    snippet: z.string().optional(),
});
export const ConversationSchema = z.object({
    id: z.string(),
    title: z.string(),
    createdAt: EpochMsSchema,
    updatedAt: EpochMsSchema,
});
export const DisplayMessageSchema = z.object({
    id: z.string(),
    role: z.enum(["user", "assistant", "system"]),
    content: z.string(),
    timestamp: EpochMsSchema,
    conversationId: z.string(),
    references: z.array(MessageReferenceSchema).optional(),
    images: z.array(MessageImageSchema).optional(),
});
// ─── Ingest / dedup queues (src/lib/ingest-queue.ts, dedup-queue.ts) ─────
export const QueueTaskStatusSchema = z.enum([
    "pending",
    "processing",
    "done",
    "failed",
]);
export const IngestTaskSchema = z.object({
    id: z.string(),
    /** Stable project UUID (project-identity.ts). */
    projectId: z.string(),
    /** Relative to project root, e.g. `raw/sources/folder/file.pdf`. */
    sourcePath: z.string(),
    /** e.g. `AI-Research > papers` or `""`. */
    folderContext: z.string(),
    status: QueueTaskStatusSchema,
    addedAt: EpochMsSchema,
    error: z.string().nullable(),
    retryCount: z.number().int().nonnegative(),
});
export const DuplicateGroupSchema = z.object({
    /** Two or more slugs from the input list. */
    slugs: z.array(z.string()).min(2),
    reason: z.string(),
    confidence: z.enum(["high", "medium", "low"]),
});
export const DedupTaskSchema = z.object({
    id: z.string(),
    projectId: z.string(),
    group: DuplicateGroupSchema,
    canonicalSlug: z.string(),
    status: QueueTaskStatusSchema,
    addedAt: EpochMsSchema,
    error: z.string().nullable(),
    retryCount: z.number().int().nonnegative(),
});
// ─── File-sync change queue (commands/file_sync.rs, camelCase serde) ─────
export const FileChangeKindSchema = z.enum(["created", "modified", "deleted"]);
export const FileChangeStatusSchema = z.enum([
    "pending",
    "processing",
    "done",
    "failed",
    "superseded",
]);
export const FileChangeTaskSchema = z.object({
    id: z.string(),
    projectId: z.string(),
    path: z.string(),
    kind: FileChangeKindSchema,
    status: FileChangeStatusSchema,
    hashBefore: z.string().nullable(),
    hashAfter: z.string().nullable(),
    size: z.number().int().nonnegative().nullable(),
    mtimeMs: z.number().int().nullable(),
    createdAt: z.number().int(),
    updatedAt: z.number().int(),
    retryCount: z.number().int().nonnegative(),
    error: z.string().nullable(),
    needsRerun: z.boolean(),
});
export const FileChangeQueueSchema = z.object({
    version: z.number().int().nonnegative(),
    tasks: z.array(FileChangeTaskSchema),
});
export const FileChangeRescanResultSchema = z.object({
    queue: FileChangeQueueSchema,
    changedTasks: z.array(FileChangeTaskSchema),
});
// ─── Source-delete cascade result (src/lib/source-lifecycle.ts) ──────────
export const SourceDeleteResultSchema = z.object({
    deletedWikiPaths: z.array(z.string()),
    rewrittenSourcePages: z.array(z.string()),
    skippedPages: z.array(z.string()),
});
//# sourceMappingURL=core.js.map